import { decodeKeysetCursor, encodeKeysetCursor, pagedQueryFields } from "../paging.js";
import { CP_AUDIT_ACTION_NAMES, CP_AUDIT_ACTOR_KINDS, CP_AUDIT_OUTCOMES, CP_AUDIT_RESOURCE_TYPE_NAMES } from "./vocabulary.js";
import { z } from "zod";
//#region src/cp-audit/read.ts
/**
* The read side of the CP audit trail: `GET /v1/admin/audit-log` and
* `GET /v1/admin/audit-log/export`, on cp-auth-api and on the site-admin-api proxy.
*
* Plan `2026-10-14-cp-audit-coverage`, decisions D-8, D-9, D-15, D-16 and D-17, rulings R-004,
* R-005 and R-015.
*
* Who uses it:
*
* - cp-auth-api binds {@link cpAuditExportQuerySchema} on the export, and returns a
*   {@link CpAuditExport}. On the list it binds `auditListQuerySchema`, which is
*   {@link cpAuditQuerySchema} plus the old `page` and `pageSize` pair for one release (R-004,
*   R-005), and returns a {@link CpAuditPage}. It encodes and decodes the list cursor with
*   {@link encodeCpAuditCursor} and {@link decodeCpAuditCursor}.
* - The site-admin-api proxy binds the two contract schemas and forwards the query unchanged, so the
*   two hops cannot disagree on a bound.
* - The Audit page in cp-admin-ui builds the query and parses the answers with
*   {@link cpAuditPageSchema} and {@link cpAuditExportSchema}.
*
* Both routes require `cp:audit:read` on both hops. The query schemas have refinements and no
* transform, so `createOpenApiDocument()` can represent them.
*/
/** The page size when the query names none: the newest 50 rows (D-8). */
const CP_AUDIT_PAGE_DEFAULT_LIMIT = 50;
/** The largest page a query may ask for. A larger `limit` answers 400; it is never clamped. */
const CP_AUDIT_PAGE_MAX_LIMIT = 200;
/** The row count up to which `count` is exact (D-15). Above it, `count` is this value. */
const CP_AUDIT_COUNT_CAP = 1e4;
/** The most rows one export returns (D-17). */
const CP_AUDIT_EXPORT_MAX_ROWS = 1e4;
/** The longest `startDate` or `endDate` text, the bound of the proxy before this plan. */
const CP_AUDIT_DATE_MAX_LENGTH = 64;
/**
* The five filters of the Audit page (D-8). Every filter is optional and exact; two or more
* filters combine with AND.
*
* - `action`: a key of `CP_AUDIT_ACTIONS` (D-16).
* - `operatorId`: the operator id the operator picker chose (D-9), as any 8-4-4-4-12 hex text. The
*   form takes the seeded system operator id `00000000-0000-0000-0000-000000000001`, which old rows
*   hold and which the picker lists (R-015).
* - `resourceType`: a key of `CP_AUDIT_RESOURCE_TYPES` (D-16).
* - `outcome`: one of `CP_AUDIT_OUTCOMES`.
* - `startDate`, `endDate`: ISO 8601 date-times with a zone, both ends inclusive. `startDate` must
*   not be later than `endDate`.
*/
const cpAuditFilterFields = {
	action: z.enum(CP_AUDIT_ACTION_NAMES).optional(),
	operatorId: z.guid().optional(),
	resourceType: z.enum(CP_AUDIT_RESOURCE_TYPE_NAMES).optional(),
	outcome: z.enum(CP_AUDIT_OUTCOMES).optional(),
	startDate: z.iso.datetime({ offset: true }).max(64).optional(),
	endDate: z.iso.datetime({ offset: true }).max(64).optional()
};
/** Refuse a range whose start is later than its end. */
function refineDateRange(query, ctx) {
	if (query.startDate === void 0 || query.endDate === void 0) return;
	if (Date.parse(query.startDate) > Date.parse(query.endDate)) ctx.addIssue({
		code: "custom",
		path: ["startDate"],
		message: "startDate must not be later than endDate"
	});
}
/**
* The strict query of `GET /v1/admin/audit-log`: the five filters, then the house paging pair.
*
* - `cursor`: the `nextCursor` of the previous page, opaque. A token that does not decode with
*   {@link decodeCpAuditCursor} answers 400, never a first page.
* - `limit`: 1 to {@link CP_AUDIT_PAGE_MAX_LIMIT}. Absent: the service applies
*   {@link CP_AUDIT_PAGE_DEFAULT_LIMIT}. The schema carries no default (see `pagedQueryFields`).
*
* Any other key answers 400.
*/
const cpAuditQuerySchema = z.object({
	...cpAuditFilterFields,
	...pagedQueryFields({ maxLimit: 200 })
}).strict().superRefine(refineDateRange);
/**
* The strict query of `GET /v1/admin/audit-log/export`: the five filters of the list, and no
* paging. The export returns at most the newest {@link CP_AUDIT_EXPORT_MAX_ROWS} rows that match.
*/
const cpAuditExportQuerySchema = z.object(cpAuditFilterFields).strict().superRefine(refineDateRange);
/**
* One row as the read API returns it.
*
* - `id`, `operatorId`: the stored UUIDs. `operatorId` is `null` when no person acted. The form is
*   any 8-4-4-4-12 hex text, because old rows hold the system operator id
*   `00000000-0000-0000-0000-000000000001`.
* - `action`, `resourceType`: the stored text. An old row can carry a string that the vocabulary
*   no longer holds, so the row form is any string; the page labels it with
*   `cpAuditActionLabel` and `cpAuditResourceTypeLabel`.
* - `actorKind`: `null` on a row written before this plan. The page shows such a row as
*   `operator` when `operatorId` is set and as unknown when it is not.
* - `actorService`: the service subject from the token of the writer, without the `svc:` prefix.
* - `operatorName`, `operatorEmail`: from a join to `cp_auth.operators` at read time. `null` when
*   the row names no operator or the operator row is gone (the column has no foreign key).
* - `resourceId`: the stored `resource_id` as text, else the stored `resource_key`, else `null`.
* - `createdAt`: an ISO 8601 date-time.
*/
const cpAuditRowSchema = z.object({
	id: z.guid(),
	createdAt: z.iso.datetime({ offset: true }),
	action: z.string(),
	actorKind: z.enum(CP_AUDIT_ACTOR_KINDS).nullable(),
	operatorId: z.guid().nullable(),
	operatorName: z.string().nullable(),
	operatorEmail: z.string().nullable(),
	actorService: z.string().nullable(),
	actorLabel: z.string().nullable(),
	resourceType: z.string().nullable(),
	resourceId: z.string().nullable(),
	outcome: z.enum(CP_AUDIT_OUTCOMES).nullable(),
	details: z.record(z.string(), z.unknown()).nullable(),
	ipAddress: z.string().nullable(),
	correlationId: z.string().nullable()
}).strict();
/**
* The answer of `GET /v1/admin/audit-log`.
*
* - `data`: at most `limit` rows, newest first by `(createdAt DESC, id DESC)`.
* - `nextCursor`: the token of the next page, or `null` when this page holds the last row.
* - `count`: the number of rows that match the filters, exact up to {@link CP_AUDIT_COUNT_CAP}.
* - `countCapped`: `true` when more than {@link CP_AUDIT_COUNT_CAP} rows match; `count` is then
*   {@link CP_AUDIT_COUNT_CAP} and the page shows "10,000+".
*/
const cpAuditPageSchema = z.object({
	data: z.array(cpAuditRowSchema),
	nextCursor: z.string().min(1).nullable(),
	count: z.number().int().min(0).max(CP_AUDIT_COUNT_CAP),
	countCapped: z.boolean()
}).strict().refine((page) => !page.countCapped || page.count === 1e4, {
	path: ["count"],
	message: `count must be ${CP_AUDIT_COUNT_CAP} when countCapped is true`
});
/**
* The answer of `GET /v1/admin/audit-log/export`, sent as JSON.
*
* - `exportedAt`: when cp-auth-api read the rows.
* - `data`: at most {@link CP_AUDIT_EXPORT_MAX_ROWS} rows, newest first.
* - `truncated`: `true` when more rows matched than `data` holds. The caller narrows the filters
*   to get the rest; the export has no cursor.
*
* Each export writes one `audit_log.exported` row (D-17).
*/
const cpAuditExportSchema = z.object({
	exportedAt: z.iso.datetime({ offset: true }),
	data: z.array(cpAuditRowSchema).max(CP_AUDIT_EXPORT_MAX_ROWS),
	truncated: z.boolean()
}).strict();
/**
* The text of a stored `created_at` in a cursor: UTC, with 0 to 6 fractional digits.
*
* Postgres keeps microseconds and a JavaScript `Date` keeps milliseconds. A cursor built from a
* `Date` cuts the last three digits, and the next page then repeats or skips the rows of that
* millisecond. cp-auth-api must read the text from SQL, for example
* `to_char(created_at AT TIME ZONE 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS.US"Z"')`, and compare it as a
* `timestamptz` in the keyset predicate.
*/
const CP_AUDIT_CURSOR_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/;
const cpAuditCursorPayloadSchema = z.object({
	t: z.string().regex(CP_AUDIT_CURSOR_TIME_PATTERN),
	i: z.guid()
}).strict();
/**
* Encode the cursor of a page from its last row.
*
* Who calls it: cp-auth-api, on the last row it keeps (never the peek row). Input: a
* {@link CpAuditCursor}. Output: an opaque base64url token. It throws a `TypeError` when
* `createdAt` does not match {@link CP_AUDIT_CURSOR_TIME_PATTERN} or `id` is not a UUID text,
* because such a token could never decode.
*/
function encodeCpAuditCursor(cursor) {
	const payload = {
		t: cursor.createdAt,
		i: cursor.id
	};
	if (!cpAuditCursorPayloadSchema.safeParse(payload).success) throw new TypeError("encodeCpAuditCursor: createdAt must be UTC microsecond text and id a UUID");
	return encodeKeysetCursor(payload);
}
/**
* Decode a cursor from the query.
*
* Who calls it: cp-auth-api, before the keyset predicate. Input: the `cursor` text. Output: the
* {@link CpAuditCursor}. It throws `InvalidPageCursorError` for any token that is not one that
* {@link encodeCpAuditCursor} wrote: bad base64url, bad JSON, a missing or extra key, or a bad
* value. cp-auth-api maps that error to 400.
*/
function decodeCpAuditCursor(token) {
	const payload = decodeKeysetCursor(cpAuditCursorPayloadSchema, token);
	return {
		createdAt: payload.t,
		id: payload.i
	};
}
//#endregion
export { CP_AUDIT_COUNT_CAP, CP_AUDIT_CURSOR_TIME_PATTERN, CP_AUDIT_DATE_MAX_LENGTH, CP_AUDIT_EXPORT_MAX_ROWS, CP_AUDIT_PAGE_DEFAULT_LIMIT, CP_AUDIT_PAGE_MAX_LIMIT, cpAuditExportQuerySchema, cpAuditExportSchema, cpAuditPageSchema, cpAuditQuerySchema, cpAuditRowSchema, decodeCpAuditCursor, encodeCpAuditCursor };

//# sourceMappingURL=read.js.map